import { touch, stopSubmit } from 'redux-form';
import { setError } from '../../actions';

/**
 * Checks if the error is a purchaser email validation error
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if this is a purchaser email validation error
 */
export const isPurchaserEmailValidationError = (error) => {
  return error?.details?.purchase_attributes?.includes('purchase.email.invalid') ?? false;
};

/**
 * Checks for the typed billing-company error returned by the purchaser PUT.
 * The key, rather than the HTTP status, is the backend contract.
 *
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if a billing company could not be resolved or created
 */
export const isBillingCompanyResolutionError = (error) => {
  const possibleErrorHashes = [
    error,
    error?.details,
    error?.errors,
    error?.response?.data,
    error?.response?.data?.details,
    error?.response?.data?.errors,
  ];

  return possibleErrorHashes.some(
    (errorHash) =>
      errorHash &&
      typeof errorHash === 'object' &&
      !Array.isArray(errorHash) &&
      Object.prototype.hasOwnProperty.call(errorHash, 'billing_company'),
  );
};

/**
 * Matches the backend's ancillary unavailability message, capturing the product code.
 * Example: "passenger.0.ancillary.DONAS1.not_available" → captures "DONAS1".
 */
const ANCILLARY_UNAVAILABLE_REGEX = /ancillary\.([^.]+)\.not_available/;

/**
 * Checks if the error is a product unavailability error.
 * Assumes the backend's current contract (code 300): an `errors` array whose messages
 * look like "passenger.N.ancillary.CODE.not_available". Falls back to `details.message`
 * when the errors array is not present.
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True if this is a product unavailability error
 */
export const isProductUnavailableError = (error) => {
  if (!error) return false;

  // Primary: errors array with ancillary not_available messages
  if (Array.isArray(error.errors) && error.errors.length > 0) {
    return error.errors.some((err) => ANCILLARY_UNAVAILABLE_REGEX.test(err?.message));
  }

  // Fallback: details.message when the errors array is not present
  return ANCILLARY_UNAVAILABLE_REGEX.test(error.details?.message ?? '');
};

/**
 * Extracts unique product codes from a product unavailability error.
 * Iterates the `errors` array (current contract) and falls back to `details.message`
 * when the array is absent or yields no codes.
 * @param {Object} error - The error object from the backend API
 * @returns {Array<string>} Deduplicated array of unavailable product codes
 */
export const extractUnavailableProductCodes = (error) => {
  if (!error) return [];

  const codesSet = new Set();

  // Primary: iterate the full errors array
  if (Array.isArray(error.errors) && error.errors.length > 0) {
    error.errors.forEach((err) => {
      const match = err?.message?.match(ANCILLARY_UNAVAILABLE_REGEX);
      if (match?.[1]) codesSet.add(match[1]);
    });
  }

  // Fallback: details.message when the errors array yielded nothing
  if (codesSet.size === 0 && error.details?.message) {
    const match = error.details.message.match(ANCILLARY_UNAVAILABLE_REGEX);
    if (match?.[1]) codesSet.add(match[1]);
  }

  return [...codesSet];
};

/**
 * Passenger-form rules whose backend message carries the failing passenger's index as its trailing
 * segment. platform-purchase's 422 body is one `{code, message}` hash per error and `map_errors`
 * drops the attribute name, so that index is the only field identity that survives the response.
 * See Api::V2::PassengerParams.
 *
 * Each rule owns both of its copy keys — `error` lands on the passenger's field, `banner` on the
 * global alert. Neither may live inside the shared matcher: a helper that hardcodes one rule's key
 * shows the wrong message the first time a second rule is added.
 *
 * The pattern carries the whole prefix and anchors the index at the end of the message. That is what
 * keeps `document_type.` from matching `document_type_second.` (a plain `includes` left the outcome
 * to `Object.keys` order) and what rejects a malformed suffix: `…invalid_for_minor.1abc` is not an
 * index, where `parseInt` would have read it as passenger 1.
 */
const INDEXED_PASSENGER_FIELD_RULES = [
  {
    pattern: /passenger\.email\.invalid\.(\d+)$/,
    field: 'email',
    error: 'email_valid',
    banner: 'passenger_email_validation_error',
  },
  {
    // A document type the passenger's age does not allow. platform-purchase owns this rule — a
    // Colombian Cédula de Ciudadanía is only issued at 18, so minors carry TI/RC — and checks both
    // document slots against the same allowlist the funnel narrows its picker with.
    pattern: /passenger\.document_type\.invalid_for_minor\.(\d+)$/,
    field: 'documentType',
    error: 'invalid_document_type_for_minor',
    banner: 'passenger_document_type_validation_error',
  },
  {
    pattern: /passenger\.document_type_second\.invalid_for_minor\.(\d+)$/,
    field: 'documentTypeSecond',
    error: 'invalid_document_type_for_minor',
    banner: 'passenger_document_type_validation_error',
  },
];

/**
 * Matches one backend message against the indexed passenger-field rules.
 * @param {string} message - A single backend error message.
 * @returns {{rule: Object, passengerIndex: number}|null} The rule and passenger index, or null.
 */
const matchIndexedPassengerFieldError = (message) => {
  if (typeof message !== 'string' || !message) return null;

  const matched = INDEXED_PASSENGER_FIELD_RULES.map((rule) => ({
    rule,
    match: message.match(rule.pattern),
  })).find(({ match }) => Boolean(match));

  if (!matched) return null;

  return { rule: matched.rule, passengerIndex: Number(matched.match[1]) };
};

/**
 * Every backend message a rejection carries, whatever shape js-api-client left it in.
 *
 * There are three, and the status decides which one arrives. `checkStatus` only special-cases
 * 404/422/500: on a 422 it throws `NetworkError(code, msg, body.errors[0], body.errors)`, so
 * `errors` is the full array and `details` is merely its first entry — reading `details.message`
 * alone there would flag the first offending passenger and silently drop the rest. On any OTHER
 * status it throws `NetworkError(status, msg, body)`, leaving `errors` null and `details` holding
 * the entire body, so a rejection with a body-level `errors` array surfaces as `details.errors`.
 * Older brands answer with a bare `details.message`. Read all three or a non-422 refusal degrades
 * to the generic `t('error.<statusText>')` banner.
 *
 * @param {Object} error - The error object from the backend API
 * @returns {Array<string>} The messages to match, in the order the backend sent them.
 */
const backendErrorMessages = (error) => {
  if (Array.isArray(error?.errors) && error.errors.length > 0) {
    return error.errors.map((entry) => entry?.message).filter(Boolean);
  }

  if (Array.isArray(error?.details?.errors) && error.details.errors.length > 0) {
    return error.details.errors.map((entry) => entry?.message).filter(Boolean);
  }

  return error?.details?.message ? [error.details.message] : [];
};

/**
 * The backend's own message for a 422 that no field rule above can place, for the global banner.
 *
 * platform-purchase localises these itself (`Api::V2::PassengerParams` adds symbols like
 * `:minor_traveling_alone`, which ActiveModel resolves against the TENANT's locale), so what arrives
 * is finished prose with no key and no index — see task 6.2 of validate-minor-policy-and-discounts.
 * Showing it verbatim is the only way the buyer learns why the save was refused.
 *
 * Read through the same three shapes as everything else in this module, so a non-422 refusal keeps
 * its own prose instead of falling back to the generic status banner.
 *
 * @param {Object} error - The error object from the backend API
 * @returns {string|null} The first backend message, or null when the 422 carries none.
 */
export const backendBannerMessage = (error) => backendErrorMessages(error)[0] ?? null;

/**
 * Turns every indexed passenger-field message in a 422 into redux-form errors.
 *
 * @param {Object} error - The error object from the backend API
 * @returns {Object|null} redux-form errors, the fields to touch and the banner copy key, or null.
 */
export const parsePassengerFieldErrors = (error) => {
  const matches = backendErrorMessages(error).map(matchIndexedPassengerFieldError).filter(Boolean);

  if (matches.length === 0) return null;

  const passengers = [];
  const fieldsToTouch = [];

  matches.forEach(({ rule, passengerIndex }) => {
    passengers[passengerIndex] = {
      ...passengers[passengerIndex],
      [rule.field]: rule.error,
    };
    fieldsToTouch.push(`passengers[${passengerIndex}].${rule.field}`);
  });

  return {
    errors: { passengers },
    fieldsToTouch,
    // Every message in one 422 comes from the same save, so the first match names the banner.
    bannerMessage: matches[0].rule.banner,
  };
};

/**
 * Whether a 422 carries a passenger-field error this module can place on a form field.
 *
 * The call sites ask this instead of naming one rule: a matcher added to
 * INDEXED_PASSENGER_FIELD_RULES then reaches the form without touching the actions again. Gating on
 * a single rule is how the minor document-type mapping shipped unreachable.
 *
 * @param {Object} error - The error object from the backend API
 * @returns {boolean} True when parsePassengerFieldErrors resolves something.
 */
export const hasParseablePassengerFieldError = (error) => parsePassengerFieldErrors(error) !== null;

/**
 * Parses backend API errors and converts them to redux-form field errors
 * This utility helps translate server-side validation errors into client-side form errors
 *
 * @param {Object} error - The error object from the backend API
 * @returns {Object|null} Returns form errors object for redux-form or null if no parseable errors
 */
export const parseBackendErrors = (error) => {
  if (!error) {
    return null;
  }

  // Passenger-form rules (passenger e-mail, minor document type) all carry the passenger index in
  // the message and share one matcher.
  const passengerFieldErrors = parsePassengerFieldErrors(error);
  if (passengerFieldErrors) return passengerFieldErrors;

  // Check for purchaser email validation errors
  if (error.details?.purchase_attributes?.includes('purchase.email.invalid')) {
    return {
      errors: {
        email: 'email_valid', // Use existing translation key
      },
      fieldsToTouch: ['email'],
      bannerMessage: 'purchaser_email_validation_error',
    };
  }

  return null;
};

/**
 * Parses a backend validation error and puts it on the form field it belongs to.
 *
 * Not email-specific despite where it started: it dispatches whatever parseBackendErrors resolves,
 * so a new rule only needs a matcher there. Used for passenger e-mail, purchaser e-mail, and the
 * minor document-type rule platform-purchase enforces.
 *
 * @param {Object} params - The parameters for handling the error
 * @param {Object} params.reason - The error object from the backend API
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {string} params.formName - The form name ('passengers' or 'purchaser')
 * @param {number} params.errorCode - The error code to use (202 for passengers, 204 for purchaser)
 * @returns {void}
 */
export const handleBackendFieldError = ({ reason, dispatch, formName, errorCode }) => {
  const errorData = parseBackendErrors(reason);
  if (errorData) {
    // Mark fields as touched so errors show
    dispatch(touch(formName, ...errorData.fieldsToTouch));

    // Use stopSubmit to set field-specific errors
    dispatch(stopSubmit(formName, errorData.errors));

    // Also show global error message with appropriate translation. Every rule that resolves names
    // its own banner copy, so a new rule needs no change here and no change at the call sites.
    dispatch(setError(errorCode, errorData.bannerMessage, 'error', false));
  }
};
